import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import LinkedText from "../linkedText/linkedText.component";

interface PageSectionProps {
  // Background band: plain white, warm cream, the paper tint, or "ink" -
  // the dark band (the preview's `.sec-ink`) with white heading and intro.
  // Pages alternate these so neighbouring sections never share a background.
  tone?: "white" | "cream" | "tint" | "ink";
  id?: string;
  // The heading's id, for aria-labelledby. Defaults to `${id}-title`, or the
  // heading text as a slug when the section has no id.
  headingId?: string;
  heading?: string;
  intro?: string;
  // For sections with no visible heading.
  ariaLabel?: string;
  children: ReactNode;
}

// Each tone sets its own text colour (not merged via cn - tailwind-merge
// doesn't treat custom names like text-ink as conflicting with text-white).
const toneClass = {
  white: "bg-white text-ink",
  cream: "bg-cream text-ink",
  tint: "bg-paper text-ink",
  ink: "bg-secondary-2 text-white",
};

// Website 2.0 content section: a background band, the site-wrap container and
// an optional heading block (h2 + intro paragraph).
const PageSection = ({
  tone = "white",
  id,
  headingId,
  heading,
  intro,
  ariaLabel,
  children,
}: PageSectionProps) => {
  const titleId =
    headingId ??
    (id ? `${id}-title` : heading?.toLowerCase().replace(/[^a-z0-9]+/g, "-"));
  return (
    <section
      id={id}
      aria-labelledby={heading ? titleId : undefined}
      aria-label={heading ? undefined : ariaLabel}
      className={cn("scroll-mt-[88px] py-16 sm:py-24", toneClass[tone])}
    >
      <div className="site-wrap">
        {heading && (
          <div className="mb-12 max-w-[720px]">
            <h2
              id={titleId}
              className="text-[clamp(1.9rem,3.6vw,2.9rem)] leading-[1.08] font-bold tracking-[-0.025em]"
            >
              {heading}
            </h2>
            {intro && (
              <p
                className={cn(
                  "mt-4 max-w-[60ch] text-[1.1rem] leading-relaxed",
                  tone === "ink" ? "text-white/80" : "text-ink-muted",
                )}
              >
                <LinkedText text={intro} />
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

export default PageSection;
